/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { Menu, ModifierGroup } from "../state/types/menuTypes";

export const uniqueModifierKey = (m: any) => m.id + "-" + m.groupId;

export interface DerivedModifierGroup extends ModifierGroup {
  modifiers: any[];
}

export interface UseModifierSelectionProps {
  menu: Menu;
  modifierGroupIds: ReadonlyArray<number>;
}

export interface UseModifierSelectionReturn {
  selectedOptions: any[];
  setSelectedOption: (modifierGroupId: number, modifier: any) => void;
  resetToDefaults: () => void;
  validateSelection: () => number[];
  deriveModifierGroups: () => DerivedModifierGroup[];
}

export function deriveModifierGroupsFromMenu(
  modifierGroupIds: ReadonlyArray<number>,
  menu: Menu
): DerivedModifierGroup[] {
  const modifierGroups = menu.modifierGroups.filter((mg) =>
    modifierGroupIds.includes(mg.id)
  );

  return modifierGroups.map((mg: ModifierGroup) => {
    const modifiers = mg.modifierIds.map((modifierId) =>
      menu.modifiers.find((m) => m.id === modifierId)
    );

    const derivedModifiers = modifiers.map((m) => ({
      ...m,
      groupName: mg.name,
      groupId: mg.id,
    }));

    return {
      ...mg,
      modifiers: derivedModifiers,
    };
  });
}

export function useModifierSelection({
  menu,
  modifierGroupIds,
}: UseModifierSelectionProps): UseModifierSelectionReturn {
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  const deriveModifierGroups = useCallback(() => {
    return deriveModifierGroupsFromMenu(modifierGroupIds, menu);
  }, [modifierGroupIds, menu]);

  const getDefaultOptions = useCallback(() => {
    const modifierGroupsDerived = deriveModifierGroups();

    return modifierGroupsDerived.reduce(
      (acc: any[], mg: DerivedModifierGroup) => {
        const defaultModifier = mg.modifiers.find(
          (m: any) => m?.id === mg.defaultModifierId
        );

        if (defaultModifier !== undefined) {
          return [
            ...acc,
            {
              id: defaultModifier.id,
              name: defaultModifier.name,
              groupName: mg.name,
              groupId: mg.id,
              price: defaultModifier.price,
            },
          ];
        }

        return acc;
      },
      []
    );
  }, [deriveModifierGroups]);

  const resetToDefaults = useCallback(() => {
    setSelectedOptions(getDefaultOptions());
  }, [getDefaultOptions]);

  const setSelectedOption = useCallback(
    (modifierGroupId: number, modifier: any) => {
      const modifierGroup = menu.modifierGroups.find(
        (mg) => mg.id === modifierGroupId
      );

      const maximumChoices = modifierGroup ? modifierGroup.maximumChoices : 0;

      setSelectedOptions((currentOptions) => {
        const selectedModifiers = currentOptions.filter(
          (m: any) => m.groupId === modifierGroupId
        );

        const isNotDuplicateModifier = !currentOptions
          .map((o) => uniqueModifierKey(o))
          .includes(uniqueModifierKey(modifier));

        if (selectedModifiers.length === maximumChoices && maximumChoices > 0) {
          if (!isNotDuplicateModifier) {
            return currentOptions.filter(
              (m: any) => m.id !== modifier.id || m.groupId !== modifier.groupId
            );
          }
          return currentOptions;
        }

        if (isNotDuplicateModifier) {
          if (modifier.id) {
            return [...currentOptions, modifier];
          }
          return currentOptions;
        } else {
          return currentOptions.filter(
            (m: any) => m.id !== modifier.id || m.groupId !== modifier.groupId
          );
        }
      });
    },
    [menu.modifierGroups]
  );

  const validateSelection = useCallback((): number[] => {
    const modifierGroupsDerived = deriveModifierGroups();

    const requiredModifierGroups = modifierGroupsDerived.filter(
      (mg: DerivedModifierGroup) => mg.minimumChoices > 0
    );

    const requiredGroupIds = requiredModifierGroups.map(
      (mg: DerivedModifierGroup) => mg.id
    );
    const selectedGroupIds = selectedOptions.map((m: any) => m.groupId);

    return requiredGroupIds.filter((id) => !selectedGroupIds.includes(id));
  }, [deriveModifierGroups, selectedOptions]);

  return {
    selectedOptions,
    setSelectedOption,
    resetToDefaults,
    validateSelection,
    deriveModifierGroups,
  };
}
